<template>
  <div class="container-modal">
    <div class="container-chatmodal">
      <div class="wrapper-title">
        <AnimationAi :infinite="true" />
        <div class="tit-h5 accent">{{ t('voice-streaming-initial-title') }}</div>
      </div>

      <Info2Column :title="t('entrance_intro_message_1')">
        <button class="change-sel-btn flex-btw pd-horizontal-3" @click="handleLanguageSelectPopup('LANGUAGE_HOME')">
          {{ sourceLanguage?.name || t('choose_language') }}
          <IconArrow class="icon" />
        </button>
      </Info2Column>

      <Info2Column v-if="callType === 'SINGLE'" :title="t('single-nickname-away')">
        <button class="change-sel-btn flex-btw pd-horizontal-3" @click="handleLanguageSelectPopup('LANGUAGE_AWAY')">
          {{ targetLanguage?.name || t('choose_language') }}
          <IconArrow class="icon" />
        </button>
      </Info2Column>

      <template v-if="'CREATE' === mode">
        <Info2Column v-if="participantOptions.length && callType !== 'MEETING'" :title="t('label_max_participants')">
          <div class="radio-options">
            <label
              class="radio-option"
              v-for="(option, optionIndex) in participantOptions"
              :key="`option_${optionIndex}`"
            >
              <input v-model="form.maxParticipants" type="radio" name="participants-number" :value="option" />
              <div class="radio-box radio-box-white">
                {{ formatNumberWithComma(option) }}{{ t('label_person_count') }}
              </div>
            </label>
          </div>
        </Info2Column>

        <Info2Column v-if="['MEETING', 'CONFERENCE'].includes(callType)" :title="t('label_screen_options')">
          <div class="wrapper-checkbox">
            <Checkbox v-model="form.options.screenMode">
              {{ t('screen_view') }}
            </Checkbox>
            <Checkbox v-model="form.options.subtitleMode">
              {{ t('subtitle_view') }}
            </Checkbox>
          </div>
        </Info2Column>

        <div class="input-close-wrap">
          <Info2Column :title="t('label_interpretation_chatroom_name')">
            <input
              type="text"
              class="input-t"
              maxlength="30"
              :placeholder="t('alert_enter_interpretation_chatroom_name')"
              v-model="form.title"
            />
            <button type="button" class="close" v-if="form.title" @click="resetChatName">
              <img src="/public/images/mobile/icon/close.svg" alt="" />
            </button>
          </Info2Column>

          <div class="error-message" v-if="form.title && (form.title.length < 2 || form.title.length > 30)">
            <IconInfo class="icon-error" />
            <p class="error">
              {{ t('alert_name_length_limit') }}
            </p>
          </div>
        </div>
      </template>

      <div class="footer">
        <div class="foot-info-wrap mb-6">
          <div v-if="unitPrice" class="fi-top flex-btw">
            <p class="accent small">
              {{ formatNumberWithComma(unitPrice) }} {{ t('credit') }}
              <span class="dim">/ {{ t('label_minute_singular') }}</span>
            </p>

            <p class="small">
              <i18n-t keypath="remaining_left" scope="global">
                <template #remaining>
                  <i18n-t keypath="hours_minutes" scope="global">
                    <template #hours>{{ formatNumberWithComma(timeRemaining.hours) }}</template>
                    <template #minutes>{{ timeRemaining.minutes }}</template>
                  </i18n-t>
                </template>
              </i18n-t>
            </p>
          </div>

          <div class="fi-bottom flex-btw" v-if="UserType.isHost(userStore.userInfo.userType)">
            <div class="auto-wrap">
              <p>{{ t('self_service_auto_refill') }}</p>

              <div class="switch-btn">
                <ToggleSwitch
                  :model-value="subscriptionStore.currentPlanDetails?.rechargeAllow"
                  @click.prevent="handleRechargeToggle"
                />
              </div>
            </div>

            <button type="button" class="gray-btn m-btn" @click="handleCreditPurchase">
              <span>{{ t('buy_credits') }}</span>
            </button>
          </div>
        </div>

        <ButtonProcess full-width :loading="loading" @click="handleChatAction">
          {{ t('label_start_interpretation') }}
        </ButtonProcess>
      </div>
    </div>

    <teleport to="body">
      <ModalSelectLanguage
        v-if="['LANGUAGE_HOME', 'LANGUAGE_AWAY'].includes(visibleModalType)"
        :current-language="selectedLanguage"
        :disabled-code="languageModalProperties.disabledCode"
        :subtitle-key="languageModalProperties.subtitleKey"
        using-custom-select
        @select="selectLanguage"
        @close="handleLanguageSelectPopup('')"
      />
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { cloneDeep, isEmpty } from 'lodash';

import { createChatRoom } from '@/api/apiService';
import AnimationAi from '@/components/animations/AnimationAi.vue';
import ModalSelectLanguage from '@/components/domains/chat/common/ModalSelectLanguage.vue';
import IconArrow from '@/components/icons/IconArrow.vue';
import IconInfo from '@/components/icons/IconInfo.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import Checkbox from '@/components/shared/base/Checkbox.vue';
import Info2Column from '@/components/shared/composite/Info2Column.vue';
import ToggleSwitch from '@/components/shared/composite/ToggleSwitch.vue';
import { useGtag } from '@/composables/shared/useGtag';
import { useNotification } from '@/composables/shared/useNotification';
import { usePayment } from '@/composables/shared/usePayment';
import { getLanguageByCode } from '@/static/languages';
import { useAlertStore } from '@/stores/alert';
import { useAudioStore } from '@/stores/audio';
import { usePaymentStore } from '@/stores/payment';
import { useSubscriptionStore } from '@/stores/subscription';
import { useToastStore } from '@/stores/toast';
import { useUserStore, UserType } from '@/stores/user';
import { Language, Nullable, TypeCall } from '@/types/common';
import { IResponseContractCustomerPricing, IResponseMultiCallsDashboard } from '@/types/response';
import { DATE_FORMAT, formatDateByLocale } from '@/utils/common/formatDateByLocale';
import {
  CHAT_KIND,
  CHAT_OPTION_PRESETS,
  CHAT_PRICE_KEY_MAP,
  DEFAULT_CHAT_CREATE_FORM,
  SERVICE_PRICE_MAP,
} from '@/utils/constants/chat';
import { formatNumberWithComma } from '@/utils/stringUtils.js';
import { validateName } from '@/utils/validators';

const props = withDefaults(
  defineProps<{
    title: string;
    mode: 'CREATE' | 'ENTER';
    callType: TypeCall;
    callInfo?: IResponseMultiCallsDashboard;
    unitPriceList: IResponseContractCustomerPricing[];
  }>(),
  {
    title: '',
    mode: 'CREATE',
    callType: 'GENERAL',
    callInfo: () => ({}) as IResponseMultiCallsDashboard,
    unitPriceList: () => [],
  }
);
const emit = defineEmits<{
  create: [res: any];
  enter: [planId: string, sourceLanguage: string, targetLanguage: string];
}>();

const { t } = useI18n();
const route = useRoute();

const toastStore = useToastStore();
const alertStore = useAlertStore();
const audioStore = useAudioStore();
const userStore = useUserStore();
const paymentStore = usePaymentStore();
const subscriptionStore = useSubscriptionStore();

const {
  openCreditAutoRechargePopup,
  openCreditBuyPopup,
  hasRegisteredCard,
  openCardRegisterPopup,
  openCardManagePopup,
  openSubscriptionPaymentPopup,
  openProUpgradePopup,
  cancelAutoRecharge,
} = usePayment();
const { alertAutoRenewDeactivate } = useNotification();
const { trackEventFunnel } = useGtag();

const loading = ref<boolean>(false);
const form = ref(cloneDeep(DEFAULT_CHAT_CREATE_FORM));
const visibleModalType = ref<'LANGUAGE_HOME' | 'LANGUAGE_AWAY' | ''>('');
const defaultTitle = ref<string>('');

const sourceLanguage = ref<Language>(getLanguageByCode(userStore.userInfo.languageCode));
const targetLanguage = ref<Language | null>(null);
const selectedLanguage = ref<Language | null>(null);

const deniedPlanKeys = computed<string[]>(() => {
  return (
    {
      FREE:
        subscriptionStore.currentCredits <= 1000 ? ['SCB_P300', 'SCB_P500', 'SCB_P1000', 'SCB_P2000', 'SCB_P3000'] : [],
    }[subscriptionStore.currentPlan] || []
  );
});

const participantOptions = computed<number[]>(() => {
  return Object.values(SERVICE_PRICE_MAP).reduce((acc: number[], { prefix, limitAttendee = 0 }) => {
    if (form.value.id === prefix && !deniedPlanKeys.value.includes(`${prefix}${limitAttendee}`)) {
      acc.push(limitAttendee);
    }
    return acc;
  }, []);
});

const resetChatName = () => {
  form.value.title = '';
};

const unitPrice = computed<number | null>(() => {
  const { id = '', maxParticipants = '' } = form.value;
  const itemPrice = props.unitPriceList.find(({ planId }) => planId === `${id}${maxParticipants}`);

  if (!itemPrice) {
    return null;
  }

  if (isEmpty(itemPrice.options)) {
    return itemPrice.prepaymentUnitPrice;
  }

  const { SCREEN = 0, SUBTITLE = 0 } = itemPrice.options;
  let priceFinal = itemPrice.prepaymentUnitPrice;

  if (form.value.options.screenMode) {
    priceFinal += SCREEN;
  }

  if (form.value.options.subtitleMode) {
    priceFinal += SUBTITLE;
  }

  return priceFinal;
});

const timeRemaining = computed<{ hours: number; minutes: number }>(() => {
  const totalMinutes = subscriptionStore.currentCredits / (unitPrice.value || 0);
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: Math.floor(totalMinutes % 60),
  };
});

const languageModalProperties = computed<Record<string, string>>(() => {
  const isAway = visibleModalType.value === 'LANGUAGE_AWAY';

  return {
    disabledCode: isAway ? sourceLanguage.value?.code : targetLanguage.value?.code,
    subtitleKey: isAway ? 'please_select_language_away' : 'please_select_language',
  };
});

const initChatForm = () => {
  defaultTitle.value = formatDateByLocale(Date.now(), DATE_FORMAT.FULL, userStore.pageLanguage);

  form.value = {
    ...form.value,
    id: CHAT_PRICE_KEY_MAP[props.callType],
    title: defaultTitle.value,
    callType: props.callType,
    options: CHAT_OPTION_PRESETS[props.callType] || DEFAULT_CHAT_CREATE_FORM.options,
  };
  form.value.maxParticipants = participantOptions.value.length > 0 ? participantOptions.value[0] : 0;

  sourceLanguage.value = getLanguageByCode(userStore.userInfo.languageCode);
  targetLanguage.value = null;
};

const handleLanguageSelectPopup = (type) => {
  visibleModalType.value = type;

  selectedLanguage.value =
    {
      LANGUAGE_HOME: sourceLanguage.value,
      LANGUAGE_AWAY: targetLanguage.value,
    }[type] || '';
};

const selectLanguage = (language: Language) => {
  if (visibleModalType.value === 'LANGUAGE_HOME') {
    sourceLanguage.value = language;
  } else if (visibleModalType.value === 'LANGUAGE_AWAY') {
    targetLanguage.value = language;
  } else {
    audioStore.selectLanguage(language.code);
  }
};

const validateChatForm = (): Nullable<string> => {
  if (!sourceLanguage.value) {
    return 'error_input_no_language';
  }

  if (CHAT_KIND.isSingle(props.callType) && !targetLanguage.value) {
    return 'please_select_language_away';
  }

  if (CHAT_KIND.isOneOf(props.callType, [CHAT_KIND.CONFERENCE, CHAT_KIND.MEETING, CHAT_KIND.GENERAL])) {
    return validateName(form.value.title);
  }

  return null;
};

const getChatFormData = (): FormData => {
  const { id = '', callType = '', title = '', options, maxParticipants = '' } = form.value;

  const formData = new FormData();
  formData.append(
    'body',
    JSON.stringify({
      plan: `${id}${maxParticipants}`,
      callType,
      title,
      description: '',
      keywords: [],
      options,
      removeEntranceLogoImage: false,
      removeCallLogoImage: false,
    })
  );
  return formData;
};

const createChat = async () => {
  const { data, success, errorCode } = await createChatRoom(getChatFormData());
  if (success) {
    toastStore.info(t('alert_create_success'));

    emit('create', { ...data[0], sourceLanguage: sourceLanguage.value.code });
  } else if (errorCode === 1021) {
    toastStore.error(t('alert_duplicate_name_exists'));
  } else {
    toastStore.error(t('failed_to_login_for_unknown_reason'));
  }
};

const enterChat = () => {
  const id = CHAT_PRICE_KEY_MAP[props.callType];
  emit('enter', `${id}1`, sourceLanguage.value.code, targetLanguage.value?.code ?? '');
};

const handleChatAction = async () => {
  const errorMessage = validateChatForm();
  if (errorMessage) {
    alertStore.alert({
      title: t('guide'),
      message: t(errorMessage),
    });

    return;
  }

  loading.value = true;

  if (props.mode === 'CREATE') {
    await createChat();
  } else if (props.mode === 'ENTER') {
    enterChat();
  }

  loading.value = false;
};

const handleCreditPurchase = async () => {
  trackEventFunnel('click_credit_buy_button');

  if (!hasRegisteredCard()) {
    const registeredCardType = await openCardRegisterPopup();
    if (!registeredCardType) {
      trackEventFunnel('close_card_register_popup');
      return;
    }
  }

  if (subscriptionStore.lastPaymentErrorCode) {
    const selectedCardType = await openCardManagePopup('PAYMENT', subscriptionStore.paymentType);
    if (!selectedCardType) {
      trackEventFunnel('close_card_select_popup');
      return;
    }
  }

  openCreditBuyPopup();
};

const handleRechargeToggle = async () => {
  trackEventFunnel('click_auto_recharge_toggle');

  if (subscriptionStore.currentPlan !== 'PRO') {
    openProUpgradePopup(handleProUpgrade);
    return;
  }

  const { rechargeAllow } = subscriptionStore.currentPlanDetails ?? { rechargeAllow: false };
  if (rechargeAllow) {
    trackEventFunnel('open_auto_recharge_cancel_popup');
    alertAutoRenewDeactivate(cancelAutoRecharge);
  } else {
    openCreditAutoRechargePopup();
  }
};

const handleProUpgrade = async () => {
  trackEventFunnel('click_pro_upgrade_button');

  if (!hasRegisteredCard()) {
    const registeredCardType = await openCardRegisterPopup();
    if (!registeredCardType) {
      return;
    }

    const selectedCardType = await openCardManagePopup('PAYMENT', registeredCardType);
    if (!selectedCardType) {
      return;
    }
  }

  openSubscriptionPaymentPopup('PRO', subscriptionStore.currentPlanDetails?.billingCycle);
};

onMounted(async () => {
  sourceLanguage.value = getLanguageByCode(userStore.userInfo.languageCode);

  const { languageAway = false } = route.query;
  if (languageAway) {
    visibleModalType.value = 'LANGUAGE_AWAY';
  }

  await paymentStore.fetchUserCardList();
});

watch(
  () => props.callType,
  () => {
    initChatForm();
  },
  { immediate: true }
);

watch(
  () => userStore.pageLanguage,
  () => {
    if (defaultTitle.value === form.value.title) {
      defaultTitle.value = formatDateByLocale(Date.now(), DATE_FORMAT.FULL, userStore.pageLanguage);
      form.value.title = defaultTitle.value;
    }
  }
);
</script>

<style lang="scss" scoped>
.container-modal {
  display: flex;
  position: relative;
  border-radius: 12px;
  width: 100%;
  padding: 2rem;
  margin: 0 auto;
  background: #fff;
  border: 1px solid $color-navy-100;
  overflow-y: auto;
  justify-content: center;

  @include respond-to(mobile-landscape) {
    height: fit-content;
    padding: 1rem;
    flex: 0 0 auto;
    -webkit-overflow-scrolling: touch;
  }
}

.content-wrapper {
  margin-top: 0;
}

.container-chatmodal {
  width: 100%;
  height: fit-content;
  background: var(--surface-glass-dim);
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  gap: 1rem;

  @include respond-to(mobile-landscape) {
    background: none;
    height: 100%;
  }
}

.wrapper-title {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.radio-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  .radio-box {
    justify-content: center;
  }
}

.wrapper-checkbox {
  display: flex;
  gap: 1rem;
}

.footer {
  .foot-info-wrap {
    border: 1px solid $color-gray-40;
    padding: 1rem;
    border-radius: $size-s;
    display: flex;
    flex-direction: column;
    gap: $size-s;
    .fi-top {
      .primary {
        font-weight: 500;
      }
    }
    .fi-bottom {
      .gray-btn {
        width: auto;
        padding: 1rem;
      }
    }
  }
  .auto-wrap {
    display: flex;
    align-items: center;
    gap: $size-s;
    p {
      white-space: nowrap;
      font-size: $font-size-psmall;
    }
  }
}

.error-message {
  display: flex;
  gap: 0.25rem;
  align-items: center;
  margin-top: 0.5rem;
}

::v-deep(.content-wrapper) {
  flex-direction: column;
}

::v-deep(.title) {
  margin-top: 0;
}

::v-deep(.content-slot) {
  flex: unset;
}

.change-sel-btn {
  background: $gray10;
  height: 3rem;
  font-size: $font-size-psmall;
  width: 100%;
  border-radius: $size-s;
  svg {
    transform: rotate(90deg);
  }
}

.input-error {
  border-color: var(--error);
  box-shadow: none;
}

.input-t {
  padding-right: 2.75rem;
}

.input-close-wrap {
  :deep(.content-slot) {
    position: relative;
    .close {
      position: absolute;
      right: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      padding: 0.25rem;
    }
  }
}
</style>
